import { CircleAlert, CircleSlash, Hourglass } from 'lucide-react';

import { formatKst } from '@/lib/datetime';
import { safeHttpHref } from '@/lib/receiptStatus';
import type { ReceiptStatus, ReceiptStatusView } from '@/types/receipt';

import { DeadlineBadge } from './DeadlineBadge';
import { StatusTag, type StatusTone } from './StatusTag';

interface ReceiptStatusBadgeProps {
  /** 서버 접수 상태 — 없으면 날짜 기준 마감 배지만 */
  receipt?: ReceiptStatusView | null;
  /** 접수 마감일(D-day 배지용) */
  endDate: string | null;
  /** 밀도 높은 행 — 아이콘을 줄이고 경고는 짧게 */
  compact?: boolean;
  /** 확인 시각·근거 링크·안내까지 표시(상세 화면) */
  detail?: boolean;
}

/** 관측으로 정해진 상태만 tone 을 따로 둔다 — 날짜 상태는 기존 마감 배지가 표현한다. */
const OBSERVED_TONE: Partial<Record<ReceiptStatus, StatusTone>> = {
  CLOSED_EARLY: 'neutral',
  UNVERIFIABLE: 'warning',
};

/**
 * 접수 상태 배지(specs/078) — 조기 마감·확인 불가가 확인된 공고는 날짜 기준 "D-3 마감 임박" 대신 관측 상태를 보인다(조기 마감 공고에 D-day 를
 * 띄우면 오안내). 그 외에는 기존 마감 배지 + 예산 소진 조기 마감 경고. 문구는 전부 서버 값이다.
 */
export function ReceiptStatusBadge({
  receipt,
  endDate,
  compact = false,
  detail = false,
}: ReceiptStatusBadgeProps) {
  const observedTone = receipt ? OBSERVED_TONE[receipt.status] : undefined;
  if (receipt && observedTone) {
    const Icon = receipt.status === 'CLOSED_EARLY' ? CircleSlash : CircleAlert;
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusTag tone={observedTone}>
          <Icon className={compact ? 'size-3' : undefined} aria-hidden="true" />
          {receipt.label}
        </StatusTag>
        {detail && <ObservationDetail receipt={receipt} />}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <DeadlineBadge endDate={endDate} compact={compact} />
      {receipt?.warning && (
        <StatusTag tone="warning" title={receipt.warning}>
          <Hourglass className={compact ? 'size-3' : undefined} aria-hidden="true" />
          {compact ? '조기 마감 가능' : receipt.warning}
        </StatusTag>
      )}
    </span>
  );
}

/** 마감 태그 — 조기 마감이 확인된 공고는 "조기 마감"(서버 라벨), 그 외 "마감". */
export function ClosedTag({ receipt }: { receipt?: ReceiptStatusView | null }) {
  return (
    <StatusTag tone="neutral">
      {receipt?.status === 'CLOSED_EARLY' ? receipt.label : '마감'}
    </StatusTag>
  );
}

function ObservationDetail({ receipt }: { receipt: ReceiptStatusView }) {
  const href = safeHttpHref(receipt.evidenceUrl);
  return (
    <span className="text-xs text-muted-foreground">
      {receipt.notice && <span className="mr-1">{receipt.notice}</span>}
      {receipt.observedAt && <span>확인 {formatKst(receipt.observedAt)}</span>}
      {href && (
        <>
          {' · '}
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            근거 보기
          </a>
        </>
      )}
    </span>
  );
}
